import React, { useState } from "react";
import { handleAmazonConnectSearchProfile, handleAmazonConnectCaseCreation } from "../DynamicBotLoader/ContactCenter/connect";

function CaseForm({ contactCenterConfig }) {
  const [formData, setFormData] = useState({
    customerName: "",
    phoneNumber: "",
    summary: "",
    case_reason: "",
    department: "DMV",    // default
    language: "English"   // default
  });

  const [profileData, setProfileData] = useState(null);
  const [errorFields, setErrorFields] = useState([]);
  const [phoneError, setPhoneError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  // Case Reason Options
  const caseReasons = [
    "Login Issue",
    "Technical Support",
    "License Renewal",
    "Registration Issues",
    // "Address Change",
    "Food Stamps Inquiry",
    "Other"
  ];

  // Handle form input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrorFields([]); // Clear field errors on change
    setPhoneError("");  // Clear phone error when user modifies input
  };

  // Generate case title dynamically with PST time
  // Generate case title dynamically with correct PST time
  const generateCaseTitle = () => {
    const now = new Date();
    
    // Convert to PST (America/Los_Angeles) using Intl.DateTimeFormat
    const pstFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true, // Use AM/PM format
    });

    // Extract formatted PST date and time
    const [{ value: month }, , { value: day }, , { value: year }, , { value: hour }, , { value: minute }, , { value: amPm }] =
      pstFormatter.formatToParts(now);

    const formattedDate = `${year}-${month}-${day}`;
    const formattedTime = `${hour}:${minute} ${amPm.toUpperCase()} PST`;

    return `${formData.customerName || "Customer"} ${formData.case_reason || "Case"} case - ${formattedDate} ${formattedTime}`;
  };

  // Handle case creation
  const handleSubmit = async (e) => {
    e.preventDefault();
    const { customerName, phoneNumber, summary, case_reason, department, language } = formData;

    // Validate required fields
    const missingFields = [];
    if (!customerName) missingFields.push("customerName");
    if (!phoneNumber) missingFields.push("phoneNumber");
    if (!summary) missingFields.push("summary");
    if (!case_reason) missingFields.push("case_reason");
    if (!department) missingFields.push("department");
    if (!language) missingFields.push("language");

    if (missingFields.length > 0) {
      setErrorFields(missingFields);
      return;
    }

    try {
      setIsSearching(true);

      const attributes = {
        Department: department,
        Language: language
      };

      const profileResponse = await handleAmazonConnectSearchProfile(contactCenterConfig, phoneNumber);

      if (!profileResponse || !profileResponse.profileId) {
        setErrorFields(["phoneNumber"]);
        setPhoneError("No customer profile found for this phone number.");
        setIsSearching(false);
        return;
      }

      setProfileData(profileResponse);
      setIsSearching(false);
      setIsSubmitting(true);

      const caseTitle = generateCaseTitle();

      const caseResponse = await handleAmazonConnectCaseCreation(
        contactCenterConfig,
        profileResponse.profileId,
        caseTitle,
        summary,
        case_reason,
        attributes
      );

      if (caseResponse?.caseId) {
        setSuccessMessage("Case created successfully!");
        setIsCompleted(true); // Hide form after successful submission
      } else {
        setErrorFields(["summary", "case_reason"]);
      }
    } catch (error) {
      console.error("Case Form Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact-section">
      <div className="container">
        <div className="row">
          <div className="col-lg-4">
            <div className="contact-box">
              <div className="section-tagline">SUBMIT A CASE</div>
              <h1 className="section-title">Need Assistance?</h1>
              <p>
                If you need support, submit a case with the details of your issue. Our team will review your request and
                get back to you as soon as possible.
              </p>
            </div>
          </div>

          <div className="col-lg-8">
            {!isCompleted ? (
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="row row-gutter-10">
                  {/* Customer Name Input */}
                  <div className="col-12 col-lg-6">
                    <input
                      type="text"
                      className={`input-text ${errorFields.includes("customerName") ? "error-border" : ""}`}
                      placeholder="Your Name"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleChange}
                      disabled={isSearching || isSubmitting}
                      aria-required="true"
                    />
                  </div>

                  {/* Phone Number Input */}
                  <div className="col-12 col-lg-6">
                    <input
                      type="text"
                      className={`input-text ${errorFields.includes("phoneNumber") ? "error-border" : ""}`}
                      placeholder="Phone Number (Format: 9991116666)"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      disabled={isSearching || isSubmitting}
                      aria-required="true"
                    />
                    {phoneError && (
                      <div className="error-box">{phoneError}</div>
                    )}
                  </div>

                  {/* Case Reason Dropdown */}
                  <div className="col-12 col-lg-6">
                    <select
                      className={`input-text ${errorFields.includes("case_reason") ? "error-border" : ""}`}
                      name="case_reason"
                      value={formData.case_reason}
                      onChange={handleChange}
                      disabled={isSearching || isSubmitting}
                      aria-required="true"
                    >
                      <option value="" disabled>Select Case Reason</option>
                      {caseReasons.map((reason) => (
                        <option key={reason} value={reason}>{reason}</option>
                      ))}
                    </select>
                  </div>

                  {/* Department Dropdown */}
                  <div className="col-12 col-lg-6">
                    <select
                      className="input-text"
                      name="department"
                      value={formData.department}
                      onChange={handleChange}
                      disabled={isSearching || isSubmitting}
                      aria-required="true"
                    >
                      <option value="DMV">DMV</option>
                      <option value="HHS">HHS</option>
                    </select>
                  </div>

                  {/* Language Dropdown */}
                  <div className="col-12 col-lg-6">
                    <select
                      className="input-text"
                      name="language"
                      value={formData.language}
                      onChange={handleChange}
                      disabled={isSearching || isSubmitting}
                      aria-required="true"
                    >
                      <option value="English">English</option>
                      <option value="Spanish">Spanish</option>
                    </select>
                  </div>

                  {/* Case Summary */}
                  <div className="col-12 col-lg-12">
                    <textarea
                      name="summary"
                      className={`input-text ${errorFields.includes("summary") ? "error-border" : ""}`}
                      placeholder="Case Summary"
                      value={formData.summary}
                      onChange={handleChange}
                      disabled={isSearching || isSubmitting}
                      aria-required="true"
                    />
                  </div>

                  {/* Auto-Generated Title */}
                  <div className="col-12 col-lg-12">
                    <p className="auto-title">Auto-Generated Title: <strong>{generateCaseTitle()}</strong></p>
                  </div>

                  {/* Error and Success Messages */}
                  <div className="col-12 col-lg-12">
                    {errorFields.length > 0 && !phoneError && <p className="error-message" style={{ color: "red" }}>Please correct the highlighted fields.</p>}
                    {successMessage && <p className="success-message" style={{ color: "green" }}>{successMessage}</p>}
                  </div>

                  {/* Submit Button */}
                  <div className="col-12 col-lg-12">
                    <button type="submit" className="btn btn-primary" disabled={isSearching || isSubmitting}>
                      {isSearching ? "Searching Profile..." : isSubmitting ? "Creating Case..." : "Submit Case"}
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <div className="success-message-container">
                <h2>🎉 Case Created Successfully!</h2>
                <p>Your request has been submitted. Our team will review it and get back to you soon.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default CaseForm;
